import type { ClientPerspective, StackablePerspective } from '@sanity/client';

/**
 * Query parameter and cookie names owned by `@sanity/preview-url-secret`, which the Studio's
 * Presentation tool uses when it opens `/preview/enable`. They are flagged `@internal` in that
 * package, so the names are mirrored here rather than imported.
 */
export const sanityPreviewPerspectiveParam = 'sanity-preview-perspective';
export const sanityPreviewPerspectiveCookie = 'sanity-preview-perspective';

/** Stacked perspectives, which is how a release is previewed, need this API version or newer. */
export const releasePerspectiveApiVersion = '2025-02-19';

const perspectiveSegmentPattern = /^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/;
const maxPerspectiveLength = 256;

/**
 * Turns the value the Studio sends (`published`, `drafts`, or a comma separated stack such as
 * `Bh5t6C0O,drafts`) into a perspective the Sanity client accepts. Returns null when the value is
 * missing or not something we are willing to query with, so callers can fall back.
 */
export const parseSanityPreviewPerspective = (value: unknown): ClientPerspective | null => {
  if (typeof value !== 'string' || value.length === 0 || value.length > maxPerspectiveLength) {
    return null;
  }

  const segments = value.split(',').map((segment) => segment.trim());
  if (segments.some((segment) => !perspectiveSegmentPattern.test(segment))) {
    return null;
  }

  // `raw` leaks unfiltered drafts alongside published documents, so it is never taken from a URL.
  if (segments.includes('raw')) {
    return null;
  }

  const onlySegment = segments.length === 1 ? segments[0] : undefined;
  if (onlySegment === 'published' || onlySegment === 'drafts' || onlySegment === 'previewDrafts') {
    return onlySegment;
  }

  return segments as StackablePerspective[];
};

export const serializeSanityPreviewPerspective = (perspective: ClientPerspective): string => {
  return Array.isArray(perspective) ? perspective.join(',') : perspective;
};

/**
 * Returns the API version the query has to run against, or null when the configured one already
 * works. Only stacked perspectives are affected; everything else predates releases.
 */
export const resolvePerspectiveApiVersion = ({
  apiVersion,
  perspective,
}: {
  apiVersion: string;
  perspective: ClientPerspective;
}): string | null => {
  if (!Array.isArray(perspective)) {
    return null;
  }

  const configured = apiVersion.replace(/^v/, '');
  if (configured === 'X' || configured >= releasePerspectiveApiVersion) {
    return null;
  }

  return releasePerspectiveApiVersion;
};
